import { Component, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from "@angular/forms";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import {
  PlanAnualTrabajo,
  PlanAnualTrabajoPayload,
  PlanAnualTrabajoService,
} from "../../../../../shared/services/plan-anual-trabajo.service";
import { AdministracionService } from "../../../../../shared/services/administracion.services";
import { Administracion } from "../../../../../shared/model/administracion.model";
import { ObjetivosPlan } from "../../../../../shared/services/objetivos-plan.service";
import { Router } from "@angular/router";
import { AuthService } from "../../../../../shared/services/auth.service";
import { forkJoin } from "rxjs";
import {
  MetasPlan,
  MetasPlanService,
} from "../../../../../shared/services/metas-plan.service";

@Component({
  selector: "app-gestion-plan",
  templateUrl: "./gestion-plan.component.html",
  styleUrls: ["./gestion-plan.component.scss"],
})
export class GestionPlanComponent implements OnInit {
  planForm!: FormGroup;
  activeTab = 1;

  guardando = false;
  cargandoListado = false;
  cargandoEdicion = false;

  planes: PlanAnualTrabajo[] = [];
  planesFiltrados: any[] = [];

  filtro = "";
  filtroEstado: number | null = null;
  pageSize = 10;
  page = 1;

  modoEdicion = false;
  idPlanEditando: number | null = null;
  planesgeneral: any[] = [];
  exportandoCompleto: { [id: number]: boolean } = {};
  empresaActual: Administracion | null = null;

  readonly fechaMaximaInicio = this.obtenerFechaAyer();

  // ===========================
  // Constantes plantilla PESV (Res. 40595 de 2022)
  // ===========================
  private readonly MESES = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];

  // NOTA: se asume que el id del catálogo de pasos (id_paso) corresponde
  // al número oficial de paso (1 a 24) de la Res. 40595 de 2022.
  // Si tu catálogo usa otro campo (ej. numero_paso), reemplázalo aquí.
  private readonly RANGOS_FASE_PESV: {
    fase: string;
    desde: number;
    hasta: number;
  }[] = [
    { fase: "FASE 1 PLANIFICACIÓN", desde: 1, hasta: 8 },
    { fase: "FASE 2 IMPLEMENTACIÓN", desde: 9, hasta: 19 },
    { fase: "FASE 3 SEGUIMIENTO", desde: 20, hasta: 22 },
    { fase: "FASE 4 MEJORA CONTINUA", desde: 23, hasta: 24 },
  ];

  private readonly COLOR_VERDE_BANDA = "FF707070"; // Bandas de sección / encabezado de fase
  private readonly COLOR_AMARILLO_P = "FFFFFF00"; // Columna "P" (Programado)
  private readonly COLOR_VERDE_E = "FF92D050"; // Columna "E" (Ejecutado)
  private readonly COLOR_GRIS_CLARO = "FFF2F2F2"; // Filas alternas

  constructor(
    private fb: FormBuilder,
    private planAnualTrabajoService: PlanAnualTrabajoService,
    private router: Router,
    private administracionService: AdministracionService,
    private authService: AuthService,
    private metasPlanService: MetasPlanService,
  ) {}

  ngOnInit(): void {
    this.inicializarFormulario();
    this.cargarPlanes();
    this.cargarEmpresaActual();
  }

  private inicializarFormulario(): void {
    this.planForm = this.fb.group({
      objetivo: ["", [Validators.required, Validators.maxLength(3000)]],
      fecha_inicio: [
        "",
        [Validators.required, this.validarFechaPasada.bind(this)],
      ],
      fecha_fin: [{ value: "", disabled: true }, Validators.required],
    });
  }

  private validarFechaPasada(
    control: AbstractControl,
  ): ValidationErrors | null {
    if (!control.value) return null;
    return control.value > this.fechaMaximaInicio
      ? { fechaFutura: true }
      : null;
  }

  private cargarEmpresaActual(): void {
    // AJUSTAR: reemplaza este método por el real de tu AuthService
    // que devuelve el nit del tenant/empresa en sesión.
    const nit =
      (this.authService as any)?.usuario?.nit ??
      (this.authService as any)?.getNitActual?.();

    if (!nit) {
      // Fallback: si no hay nit en sesión, toma la primera administración disponible
      this.administracionService.obtenerTodas().subscribe({
        next: (lista) => {
          this.empresaActual =
            Array.isArray(lista) && lista.length ? lista[0] : null;
        },
        error: () => {
          this.empresaActual = null;
        },
      });
      return;
    }

    this.administracionService.obtenerPorNit(Number(nit)).subscribe({
      next: (data) => {
        this.empresaActual = data;
      },
      error: () => {
        this.empresaActual = null;
      },
    });
  }

  onFechaInicioChange(): void {
    const fechaInicio = String(
      this.planForm.get("fecha_inicio")?.value || "",
    ).trim();
    if (!fechaInicio) {
      this.planForm.patchValue({ fecha_fin: "" }, { emitEvent: false });
      return;
    }
    const fechaFin = this.calcularFechaFin(fechaInicio);
    this.planForm.patchValue({ fecha_fin: fechaFin }, { emitEvent: false });
  }

  private calcularFechaFin(fechaInicio: string): string {
    const [year, month, day] = fechaInicio.split("-").map(Number);
    const fecha = new Date(year, month - 1, day);
    fecha.setFullYear(fecha.getFullYear() + 1);
    return this.formatearFecha(fecha);
  }

  /**
   * Obtiene la fecha actual en la zona horaria de Colombia (America/Bogota, UTC-5, sin DST),
   * sin depender de la zona horaria del navegador o del servidor donde corra el código.
   */
  private obtenerFechaHoyColombia(): Date {
    const ahora = new Date();
    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Bogota",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const [year, month, day] = formatter.format(ahora).split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  private obtenerFechaAyer(): string {
    const fecha = this.obtenerFechaHoyColombia();
    fecha.setDate(fecha.getDate());
    return this.formatearFecha(fecha);
  }

  private formatearFecha(fecha: Date): string {
    const year = fecha.getFullYear();
    const month = String(fecha.getMonth() + 1).padStart(2, "0");
    const day = String(fecha.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  // ===========================
  // Listado
  // ===========================
  cargarPlanes(): void {
    this.cargandoListado = true;
    this.planAnualTrabajoService.getAll().subscribe({
      next: (response: any) => {
        this.planes = Array.isArray(response) ? response : response?.data || [];
        this.aplicarFiltro();
        this.cargandoListado = false;
      },
      error: () => {
        this.planes = [];
        this.planesFiltrados = [];
        this.cargandoListado = false;
      },
    });
  }

  aplicarFiltro(): void {
    const termino = String(this.filtro || "")
      .toLowerCase()
      .trim();

    let filtrados = this.planes.filter((item: any) => {
      const coincideTexto =
        !termino ||
        [item.plan, item.objetivo]
          .filter((valor) => valor !== null && valor !== undefined)
          .some((valor) => String(valor).toLowerCase().includes(termino));

      const estadoItem = Number(item.estado);
      const coincideEstado =
        this.filtroEstado === null || estadoItem === this.filtroEstado;

      return coincideTexto && coincideEstado;
    });

    filtrados = filtrados.sort((a: any, b: any) => {
      const estadoA = Number(a.estado) === 1 ? 0 : 1;
      const estadoB = Number(b.estado) === 1 ? 0 : 1;
      if (estadoA !== estadoB) return estadoA - estadoB;
      return (b.id || 0) - (a.id || 0);
    });

    this.planesFiltrados = filtrados;
    this.page = 1;
  }

  // ===========================
  // Guardar / Actualizar
  // ===========================
  guardar(): void {
    if (this.planForm.invalid) {
      this.planForm.markAllAsTouched();
      Swal.fire(
        "Campos obligatorios",
        "Completa todos los campos requeridos para guardar.",
        "warning",
      );
      return;
    }

    if (this.modoEdicion) {
      this.actualizarPlan();
      return;
    }

    const formValue = this.planForm.getRawValue();
    const fechaInicio = String(formValue.fecha_inicio || "").trim();

    if (!fechaInicio || fechaInicio > this.fechaMaximaInicio) {
      Swal.fire(
        "Fecha inválida",
        "La fecha de inicio debe ser un día pasado.",
        "warning",
      );
      return;
    }

    const payload: PlanAnualTrabajoPayload = {
      plan: String(formValue.plan || "").trim(),
      objetivo: String(formValue.objetivo || "").trim() as any,
      fecha_inicio: fechaInicio,
      fecha_fin: this.calcularFechaFin(fechaInicio),
      estado: 1,
    };

    this.guardando = true;
    this.planAnualTrabajoService.create(payload).subscribe({
      next: () => {
        this.guardando = false;
        Swal.fire(
          "Éxito",
          "Plan anual de trabajo guardado correctamente.",
          "success",
        );
        this.resetearFormulario();
        this.cargarPlanes();
        this.activeTab = 2;
      },
      error: (error: any) => {
        this.guardando = false;
        const mensaje =
          error?.error?.error || "No se pudo guardar el plan anual de trabajo.";
        Swal.fire("Error", mensaje, "error");
      },
    });
  }

  private actualizarPlan(): void {
    if (!this.idPlanEditando) {
      Swal.fire("Error", "No hay un plan seleccionado para editar.", "error");
      return;
    }

    const formValue = this.planForm.getRawValue();

    const payload: Partial<PlanAnualTrabajoPayload> = {
      plan: String(formValue.plan || "").trim(),
      objetivo: String(formValue.objetivo || "").trim() as any,
    };

    this.guardando = true;
    this.planAnualTrabajoService
      .update(this.idPlanEditando, payload)
      .subscribe({
        next: () => {
          this.guardando = false;
          Swal.fire("Éxito", "Plan actualizado correctamente.", "success");
          this.cancelarEdicion();
          this.cargarPlanes();
          this.activeTab = 2;
        },
        error: (error: any) => {
          this.guardando = false;
          const mensaje =
            error?.error?.error || "No se pudo actualizar el plan.";
          Swal.fire("Error", mensaje, "error");
        },
      });
  }

  private resetearFormulario(): void {
    this.planForm.reset();
  }

  editarPlan(plan: any): void {
    if (Number(plan?.estado) !== 1) {
      Swal.fire(
        "Info",
        "Solo se puede editar cuando el registro está activo.",
        "info",
      );
      return;
    }

    const id = Number(plan?.id);
    if (!id) {
      Swal.fire("Error", "No se encontró el id del plan.", "error");
      return;
    }

    this.cargandoEdicion = true;

    this.planAnualTrabajoService.getById(id).subscribe({
      next: (data: any) => {
        this.cargandoEdicion = false;

        if (!data) {
          Swal.fire(
            "Error",
            "No se pudo cargar la información del plan.",
            "error",
          );
          return;
        }

        this.modoEdicion = true;
        this.idPlanEditando = id;

        this.planForm.get("fecha_inicio")?.disable({ emitEvent: false });

        this.planForm.patchValue({
          plan: data?.plan ?? "",
          objetivo: data?.objetivo ?? "",
          fecha_inicio: data?.fecha_inicio ?? "",
          fecha_fin: data?.fecha_fin ?? "",
        });

        this.activeTab = 1;

        Swal.fire({
          icon: "info",
          title: "Editando plan",
          text: "Los datos se cargaron en el formulario. Modifica lo necesario y presiona Actualizar.",
          timer: 2200,
          showConfirmButton: false,
        });
      },
      error: (error: any) => {
        this.cargandoEdicion = false;
        const mensaje =
          error?.error?.error || "No se pudo cargar el plan para editar.";
        Swal.fire("Error", mensaje, "error");
      },
    });
  }

  cancelarEdicion(): void {
    this.modoEdicion = false;
    this.idPlanEditando = null;
    this.planForm.get("fecha_inicio")?.enable({ emitEvent: false });
    this.planForm.reset();
  }

  cambiarEstado(plan: any, estado: number): void {
    const id = Number(plan?.id);
    if (!id) {
      Swal.fire("Error", "No se encontró el id del plan.", "error");
      return;
    }

    const accion = estado === 1 ? "activar" : "inactivar";
    Swal.fire({
      title: `¿Deseas ${accion} este plan?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Sí, ${accion}`,
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed) return;

      const request$ =
        estado === 1
          ? this.planAnualTrabajoService.activar(id)
          : this.planAnualTrabajoService.inactivar(id);

      request$.subscribe({
        next: () => {
          Swal.fire("Éxito", `Plan ${accion}do correctamente.`, "success");
          this.cargarPlanes();
        },
        error: (error: any) => {
          const mensaje =
            error?.error?.error || `No se pudo ${accion} el plan.`;
          Swal.fire("Error", mensaje, "error");
        },
      });
    });
  }

  // ===========================
  // Exportación simple (listado)
  // ===========================
  private formatDateTodo(fecha: any): string {
    if (!fecha) return "N/A";
    const d = new Date(fecha);
    return `${String(d.getUTCDate()).padStart(2, "0")}/${String(d.getUTCMonth() + 1).padStart(2, "0")}/${d.getUTCFullYear()}`;
  }

  exportarExcel(): void {
    const datos = this.planesFiltrados || [];
    if (!datos.length) {
      Swal.fire("Sin datos", "No hay planes para exportar", "info");
      return;
    }

    const filas = datos.map((item: any) => ({
      PLAN: item?.plan || "",
      OBJETIVO: item?.objetivo || "",
      INICIO: this.formatDateTodo(item?.fecha_inicio),
      FIN: this.formatDateTodo(item?.fecha_fin),
      ESTADO: Number(item?.estado) === 1 ? "ACTIVO" : "INACTIVO",
    }));

    const ws = XLSX.utils.json_to_sheet(filas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "PlanAnualTrabajo");
    XLSX.writeFile(wb, `plan_anual_trabajo_${Date.now()}.xlsx`);
    Swal.fire("Éxito", "Exportación a Excel completada", "success");
  }

  exportarPdf(): void {
    const datos = this.planesFiltrados || [];
    if (!datos.length) {
      Swal.fire("Sin datos", "No hay planes para exportar", "info");
      return;
    }

    const doc = new jsPDF("l", "pt");
    doc.setFontSize(12);
    doc.text("Listado de Planes Anuales de Trabajo", 40, 35);

    autoTable(doc, {
      head: [["PLAN", "OBJETIVO", "INICIO", "FIN", "ESTADO"]],
      body: datos.map((item: any) => [
        item?.plan || "",
        item?.objetivo || "",
        this.formatDateTodo(item?.fecha_inicio),
        this.formatDateTodo(item?.fecha_fin),
        Number(item?.estado) === 1 ? "ACTIVO" : "INACTIVO",
      ]),
      startY: 50,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [13, 110, 253] },
    });

    doc.save(`plan_anual_trabajo_${Date.now()}.pdf`);
    Swal.fire("Éxito", "Exportación a PDF completada", "success");
  }

  IrGestion(planAnualTrabajo: PlanAnualTrabajo): void {
    Swal.fire({
      title: "Crear Gestión",
      text: `¿Desea ver gestión para el plan ${planAnualTrabajo.id}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, Ver",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        this.router.navigate([
          "gestión-general/gestión-general",
          planAnualTrabajo.id,
        ]);
      }
    });
  }

  IrPasosPlan(planAnualTrabajo: PlanAnualTrabajo): void {
    Swal.fire({
      title: "Crear Pasos",
      text: `¿Desea ver pasos para el plan ${planAnualTrabajo.id}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, Ver",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        this.router.navigate(["pasos-plan/pasos-plan", planAnualTrabajo.id]);
      }
    });
  }

  private fusionarSeguro(
    ws: ExcelJS.Worksheet,
    filaIni: number,
    colIni: number,
    filaFin: number,
    colFin: number,
  ): void {
    // Evita fusionar un rango de una sola celda (innecesario y a veces problemático)
    if (filaIni === filaFin && colIni === colFin) return;

    try {
      ws.mergeCells(filaIni, colIni, filaFin, colFin);
    } catch (e) {
      // Si el rango ya estaba fusionado (solape), lo ignoramos en vez de romper el libro
      console.warn(
        `No se pudo fusionar rango (${filaIni},${colIni})-(${filaFin},${colFin}):`,
        e,
      );
    }
  }

  // ===========================================================
  // Exportación EXCEL COMPLETA con formato tipo plantilla PESV
  // (bandas verdes, fusiones, columnas por mes P/E) usando ExcelJS
  // ===========================================================
  exportarExcelCompleto(plan: any): void {
    const id = Number(plan?.id);
    if (!id) {
      Swal.fire("Error", "No se encontró el id del plan.", "error");
      return;
    }

    this.exportandoCompleto[id] = true;

    forkJoin({
      data: this.planAnualTrabajoService.getPlanGeneral(id),
      metas: this.metasPlanService.getAll(),
    }).subscribe({
      next: async ({ data, metas }: { data: any; metas: MetasPlan[] }) => {
        if (!data) {
          this.exportandoCompleto[id] = false;
          Swal.fire(
            "Sin datos",
            "No se encontró información del plan.",
            "info",
          );
          return;
        }

        try {
          const metasActivas = (metas || []).filter(
            (m) => Number(m.estado) === 1,
          );
          await this.construirExcelCompletoPlantilla(data, metasActivas);
          Swal.fire(
            "Éxito",
            "Exportación completa a Excel generada correctamente.",
            "success",
          );
        } catch (e) {
          Swal.fire(
            "Error",
            "Ocurrió un error generando el archivo Excel.",
            "error",
          );
        } finally {
          this.exportandoCompleto[id] = false;
        }
      },
      error: (error: any) => {
        this.exportandoCompleto[id] = false;
        const mensaje =
          error?.error?.error ||
          "No se pudo cargar la información completa del plan.";
        Swal.fire("Error", mensaje, "error");
      },
    });
  }

  private aplicarBordeRango(
    ws: ExcelJS.Worksheet,
    filaIni: number,
    colIni: number,
    filaFin: number,
    colFin: number,
  ): void {
    for (let f = filaIni; f <= filaFin; f++) {
      for (let c = colIni; c <= colFin; c++) {
        ws.getCell(f, c).border = this.bordeDelgado();
      }
    }
  }

  private construirTituloEmpresa(): string {
    const razonSocial =
      (this.empresaActual as any)?.razon_social ??
      (this.empresaActual as any)?.nombre ??
      (this.empresaActual as any)?.empresa ??
      "EMPRESA";

    const nit = (this.empresaActual as any)?.nit
      ? `NIT. ${(this.empresaActual as any).nit}`
      : "";

    return `${razonSocial}${nit ? "\n" + nit : ""}`;
  }

  // ---------------------------------------------------------
  // Construcción del libro completo con ExcelJS
  // ---------------------------------------------------------
  private async construirExcelCompletoPlantilla(
    data: any,
    metas: MetasPlan[],
  ): Promise<void> {
    const wb = new ExcelJS.Workbook();
    wb.creator = "PESV";
    wb.created = new Date();

    this.crearHojaObjetivosMetasRecursos(wb, data, metas);

    // Agrupa los pasos/actividades por Fase (Res. 40595 de 2022)
    const pasos = Array.isArray(data?.ActividadPasosPlanData)
      ? data.ActividadPasosPlanData
      : [];
    const gruposPorFase = new Map<string, any[]>();
    this.RANGOS_FASE_PESV.forEach((r) => gruposPorFase.set(r.fase, []));
    gruposPorFase.set("SIN FASE ASIGNADA", []);

    pasos.forEach((p: any) => {
      const idPaso = Number(p?.id_paso);
      const fase = this.obtenerFasePorIdPaso(idPaso);
      const lista = gruposPorFase.get(fase) ?? [];
      lista.push(p);
      gruposPorFase.set(fase, lista);
    });

    // Una hoja tipo "FASE X ..." por cada fase que tenga pasos
    gruposPorFase.forEach((pasosFase, nombreFase) => {
      if (!pasosFase.length) return;
      this.crearHojaFasePlantilla(wb, data, nombreFase, pasosFase);
    });

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(
      blob,
      `plan_anual_trabajo_completo_${data?.id ?? ""}_${Date.now()}.xlsx`,
    );
  }

  private obtenerFasePorIdPaso(idPaso: number): string {
    const rango = this.RANGOS_FASE_PESV.find(
      (r) => idPaso >= r.desde && idPaso <= r.hasta,
    );
    return rango ? rango.fase : "SIN FASE ASIGNADA";
  }

  private obtenerMesIndex(fecha: any): number | null {
    if (!fecha) return null;
    const d = new Date(fecha);
    if (isNaN(d.getTime())) return null;
    return d.getUTCMonth(); // 0 = Enero ... 11 = Diciembre
  }

  // ---------------------------------------------------------
  // Hoja 1: OBJETIVOS, METAS, RECURSOS (bandas verdes, estilo plantilla)
  // ---------------------------------------------------------
  private crearHojaObjetivosMetasRecursos(
    wb: ExcelJS.Workbook,
    data: any,
    metas: MetasPlan[],
  ): void {
    const ws = wb.addWorksheet("OBJETIVOS, METAS, RECURSOS");

    const TOTAL_COLS = 8; // B..I (deja A como margen, como en tu plantilla)
    ws.getColumn(1).width = 3;
    for (let c = 2; c <= TOTAL_COLS; c++) ws.getColumn(c).width = 22;

    let fila = 1;

    // ---- Encabezado empresa (fusionado) ----
    ws.mergeCells(fila, 2, fila + 3, TOTAL_COLS);
    const celdaTitulo = ws.getCell(fila, 2);
    celdaTitulo.value = `${this.construirTituloEmpresa()}\nPLAN ESTRATÉGICO DE SEGURIDAD VIAL\nPLAN DE TRABAJO ANUAL FASE 2 - PASO 9`;
    celdaTitulo.alignment = {
      horizontal: "center",
      vertical: "middle",
      wrapText: true,
    };
    celdaTitulo.font = { bold: true, size: 12 };
    this.aplicarBordeRango(ws, 1, 2, 4, TOTAL_COLS);
    fila += 5;

    // ---- Banda: OBJETIVO ----
    fila = this.escribirBandaSeccion(ws, fila, TOTAL_COLS, "OBJETIVO");
    fila = this.escribirTextoLargo(
      ws,
      fila,
      TOTAL_COLS,
      data?.objetivo || "Sin objetivo definido.",
    );
    fila += 1;

    // ---- Banda: OBJETIVOS ESPECÍFICOS ----
    fila = this.escribirBandaSeccion(
      ws,
      fila,
      TOTAL_COLS,
      "OBJETIVOS ESPECÍFICOS",
    );
    const objetivos = Array.isArray(data?.ObjetivosPlanData)
      ? data.ObjetivosPlanData
      : [];
    if (!objetivos.length) {
      fila = this.escribirTextoLargo(ws, fila, TOTAL_COLS, "Sin registros.");
    } else {
      objetivos.forEach((o: any, idx: number) => {
        fila = this.escribirTextoLargo(
          ws,
          fila,
          TOTAL_COLS,
          `${idx + 1}. ${o?.objetivos ?? ""}`,
        );
      });
    }
    fila += 1;

    // ---- Banda: METAS (con fecha programada + fecha del último seguimiento) ----
    fila = this.escribirBandaSeccion(ws, fila, TOTAL_COLS, "METAS");
    fila = this.escribirMetas(ws, fila, TOTAL_COLS, metas);
    fila += 1;

    // ---- Banda: RESPONSABILIDADES (tabla CARGO fusionado + RESPONSABILIDAD) ----
    fila = this.escribirBandaSeccion(ws, fila, TOTAL_COLS, "RESPONSABILIDADES");
    const responsabilidades = Array.isArray(data?.ResponsabilidadPlanData)
      ? data.ResponsabilidadPlanData
      : [];
    fila = this.escribirTablaResponsabilidades(
      ws,
      fila,
      TOTAL_COLS,
      responsabilidades,
    );
    fila += 1;

    // ---- Banda: INDICADORES (manuales, cargados por el usuario) ----
    fila = this.escribirBandaSeccion(ws, fila, TOTAL_COLS, "INDICADORES");
    fila = this.escribirIndicadoresManuales(
      ws,
      fila,
      TOTAL_COLS,
      Array.isArray(data?.IndicadoresPlanData) ? data.IndicadoresPlanData : [],
    );
    fila += 1;

    // ---- Banda: INDICADOR AUTOMÁTICO DEL PASO 9 ----
    const { AEPlan, APPlan, porcentaje } =
      this.calcularIndicadorCumplimientoActividades(data);

    fila = this.escribirIndicadorAutomatico(
      ws,
      fila,
      TOTAL_COLS,
      "INDICADOR PASO 9 - CUMPLIMIENTO PLAN ANUAL DE TRABAJO",
      "AEPlan / APPlan x 100",
      [
        ["AEPlan (actividades ejecutadas)", `${AEPlan}`],
        ["APPlan (actividades programadas)", `${APPlan}`],
      ],
      "100%",
      `${porcentaje}%`,
    );
    fila += 1;

    // ---- Banda: INDICADOR - CUMPLIMIENTO DE METAS PESV ----
    const { MA, TM, porcentaje: porcentajeMetas } =
      this.calcularIndicadorCumplimientoMetas(metas);

    this.escribirIndicadorAutomatico(
      ws,
      fila,
      TOTAL_COLS,
      "INDICADOR - CUMPLIMIENTO DE METAS PESV",
      "MA / TM x 100",
      [
        ["MA (metas alcanzadas)", `${MA}`], 
        ["TM (total de metas)", `${TM}`],
      ],
      "100%",
      `${porcentajeMetas}%`,
    );
  }

  // ---------------------------------------------------------
  // Helpers de estilo para la Hoja 1
  // ---------------------------------------------------------
  private escribirBandaSeccion(
    ws: ExcelJS.Worksheet,
    fila: number,
    totalCols: number,
    texto: string,
  ): number {
    this.fusionarSeguro(ws, fila, 2, fila, totalCols);
    const celda = ws.getCell(fila, 2);
    celda.value = texto;
    celda.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 14 };
    celda.alignment = { horizontal: "center", vertical: "middle" };
    celda.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: this.COLOR_VERDE_BANDA },
    };
    this.aplicarBordeRango(ws, fila, 2, fila, totalCols);
    return fila + 1;
  }

  private escribirSubtitulo(
    ws: ExcelJS.Worksheet,
    fila: number,
    totalCols: number,
    texto: string,
  ): number {
    this.fusionarSeguro(ws, fila, 2, fila, totalCols);
    const celda = ws.getCell(fila, 2);
    celda.value = texto;
    celda.font = { bold: true };
    celda.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: this.COLOR_GRIS_CLARO },
    };
    this.aplicarBordeRango(ws, fila, 2, fila, totalCols);
    return fila + 1;
  }

  private escribirTextoLargo(
    ws: ExcelJS.Worksheet,
    fila: number,
    totalCols: number,
    texto: string,
  ): number {
    this.fusionarSeguro(ws, fila, 2, fila, totalCols);
    const celda = ws.getCell(fila, 2);
    celda.value = texto;
    celda.alignment = { wrapText: true, vertical: "top" };
    this.aplicarBordeRango(ws, fila, 2, fila, totalCols);
    return fila + 1;
  }

  private escribirFilaDosColumnas(
    ws: ExcelJS.Worksheet,
    fila: number,
    totalCols: number,
    col1: string,
    col2: string,
  ): number {
    this.fusionarSeguro(ws, fila, 2, fila, 3);
    ws.getCell(fila, 2).value = col1;

    this.fusionarSeguro(ws, fila, 4, fila, totalCols);
    const celda2 = ws.getCell(fila, 4);
    celda2.value = col2;
    celda2.alignment = { wrapText: true };

    this.aplicarBordeRango(ws, fila, 2, fila, totalCols);
    return fila + 1;
  }

  /** Escribe el listado de metas (descripción + fecha programada + fecha del último seguimiento). */
  private escribirMetas(
    ws: ExcelJS.Worksheet,
    fila: number,
    totalCols: number,
    metas: MetasPlan[],
  ): number {
    if (!metas.length) {
      return this.escribirFilaDosColumnas(ws, fila, totalCols, "Sin registros", "");
    }

    metas.forEach((m: any) => {
      const fechaEjecucion = this.obtenerFechaEjecucionMeta(m);
      const fechaProgramada = this.formatearFechaCorta(m?.fecha_programacion);
      const estadoTexto = fechaEjecucion
        ? `Ejecutada: ${this.formatearFechaCorta(fechaEjecucion)}`
        : "Pendiente";

      fila = this.escribirFilaDosColumnas(
        ws,
        fila,
        totalCols,
        m?.TipoMetaPlanData?.descripcion ?? "META",
        `${m?.descripcion ?? ""}  |  Programada: ${fechaProgramada}  |  ${estadoTexto}`,
      );
    });

    return fila;
  }

  /** Escribe el bloque de indicadores manuales (nombre, fórmula, numerador/denominador, meta, periodicidad). */
  private escribirIndicadoresManuales(
    ws: ExcelJS.Worksheet,
    fila: number,
    totalCols: number,
    indicadores: any[],
  ): number {
    if (!indicadores.length) {
      return this.escribirTextoLargo(ws, fila, totalCols, "Sin registros.");
    }

    indicadores.forEach((i: any) => {
      fila = this.escribirSubtitulo(ws, fila, totalCols, `${i?.nombre_indicador ?? ""} :`);

      this.fusionarSeguro(ws, fila, 2, fila, 3);
      const celdaFormula = ws.getCell(fila, 2);
      celdaFormula.value = `${i?.tipo_calculo ?? ""}  =`;
      celdaFormula.font = { italic: true };

      ws.getCell(fila, 4).value = i?.numerador ?? "";
      ws.getCell(fila, 5).value = "X";
      ws.getCell(fila, 6).value = i?.factor ?? 100;
      fila++;

      ws.getCell(fila, 4).value = i?.denominador ?? "";
      fila++;

      ws.getCell(fila, 2).value = "META :";
      ws.getCell(fila, 2).font = { bold: true };
      ws.getCell(fila, 3).value = i?.meta ?? "";
      ws.getCell(fila, 4).value = `Periodicidad: ${i?.periodicidad ?? ""}`;
      fila += 2;
    });

    return fila;
  }

  /** Escribe una banda de indicador automático completa: título, fórmula, variables, meta y valor calculado. */
  private escribirIndicadorAutomatico(
    ws: ExcelJS.Worksheet,
    fila: number,
    totalCols: number,
    titulo: string,
    formula: string,
    variables: [string, string][],
    meta: string,
    valorCalculado: string,
  ): number {
    fila = this.escribirBandaSeccion(ws, fila, totalCols, titulo);
    fila = this.escribirFilaDosColumnas(ws, fila, totalCols, "Fórmula", formula);
    variables.forEach(([etiqueta, valor]) => {
      fila = this.escribirFilaDosColumnas(ws, fila, totalCols, etiqueta, valor);
    });
    fila = this.escribirFilaDosColumnas(ws, fila, totalCols, "Meta", meta);
    fila = this.escribirFilaDosColumnas(ws, fila, totalCols, "Valor calculado", valorCalculado);
    return fila;
  }

  private escribirTablaResponsabilidades(
    ws: ExcelJS.Worksheet,
    fila: number,
    totalCols: number,
    responsabilidades: any[],
  ): number {
    const COL_CARGO_INI = 2;
    const COL_CARGO_FIN = 3;
    const COL_RESP_INI = 4;
    const COL_RESP_FIN = totalCols;

    // ---- Encabezado de la tabla ----
    this.fusionarSeguro(ws, fila, COL_CARGO_INI, fila, COL_CARGO_FIN);
    const celdaHeadCargo = ws.getCell(fila, COL_CARGO_INI);
    celdaHeadCargo.value = "CARGO";
    celdaHeadCargo.font = { bold: true };
    celdaHeadCargo.alignment = { horizontal: "center", vertical: "middle" };
    celdaHeadCargo.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: this.COLOR_GRIS_CLARO },
    };

    this.fusionarSeguro(ws, fila, COL_RESP_INI, fila, COL_RESP_FIN);
    const celdaHeadResp = ws.getCell(fila, COL_RESP_INI);
    celdaHeadResp.value = "RESPONSABILIDAD";
    celdaHeadResp.font = { bold: true };
    celdaHeadResp.alignment = { horizontal: "center", vertical: "middle" };
    celdaHeadResp.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: this.COLOR_GRIS_CLARO },
    };

    this.aplicarBordeRango(ws, fila, COL_CARGO_INI, fila, COL_RESP_FIN);
    fila += 1;

    // ---- Sin registros ----
    if (!responsabilidades.length) {
      this.fusionarSeguro(ws, fila, COL_CARGO_INI, fila, COL_CARGO_FIN);
      ws.getCell(fila, COL_CARGO_INI).value = "Sin registros";

      this.fusionarSeguro(ws, fila, COL_RESP_INI, fila, COL_RESP_FIN);
      ws.getCell(fila, COL_RESP_INI).value = "";

      this.aplicarBordeRango(ws, fila, COL_CARGO_INI, fila, COL_RESP_FIN);
      return fila + 1;
    }

    // ---- Una fila por responsabilidad, CARGO fusionado verticalmente ----
    responsabilidades.forEach((r: any) => {
      const cargo = r?.CargosData?.cargos ?? "";
      const detalles = Array.isArray(r?.ResponsabilidadData)
        ? r.ResponsabilidadData
        : [];
      const filaInicioCargo = fila;

      if (!detalles.length) {
        this.fusionarSeguro(ws, fila, COL_RESP_INI, fila, COL_RESP_FIN);
        ws.getCell(fila, COL_RESP_INI).value = "";
        fila += 1;
      } else {
        detalles.forEach((d: any) => {
          this.fusionarSeguro(ws, fila, COL_RESP_INI, fila, COL_RESP_FIN);
          const celdaResp = ws.getCell(fila, COL_RESP_INI);
          celdaResp.value = d?.descripcion ?? "";
          celdaResp.alignment = { wrapText: true, vertical: "top" };
          fila += 1;
        });
      }

      const filaFinCargo = fila - 1;

      this.fusionarSeguro(
        ws,
        filaInicioCargo,
        COL_CARGO_INI,
        filaFinCargo,
        COL_CARGO_FIN,
      );
      const celdaCargo = ws.getCell(filaInicioCargo, COL_CARGO_INI);
      celdaCargo.value = cargo;
      celdaCargo.font = { bold: true };
      celdaCargo.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };

      this.aplicarBordeRango(
        ws,
        filaInicioCargo,
        COL_CARGO_INI,
        filaFinCargo,
        COL_RESP_FIN,
      );
    });

    return fila;
  }

  // ---------------------------------------------------------
  // Hoja por Fase: matriz PASO / ACTIVIDAD / RESPONSABLE / 12 meses (P|E)
  // ---------------------------------------------------------
  private crearHojaFasePlantilla(
    wb: ExcelJS.Workbook,
    data: any,
    nombreFase: string,
    pasosFase: any[],
  ): void {
    const ws = wb.addWorksheet(nombreFase.substring(0, 31));

    const COL_PASO = 2; // B
    const COL_ACTIVIDAD = 3; // C
    const COL_RESPONSABLE = 4; // D
    const COL_MESES_INICIO = 5; // E (cada mes ocupa 2 columnas: P y E)
    const TOTAL_COLS = COL_MESES_INICIO + this.MESES.length * 2 - 1; // hasta AB

    ws.getColumn(1).width = 3;
    ws.getColumn(COL_PASO).width = 22;
    ws.getColumn(COL_ACTIVIDAD).width = 45;
    ws.getColumn(COL_RESPONSABLE).width = 40;
    for (let c = COL_MESES_INICIO; c <= TOTAL_COLS; c++)
      ws.getColumn(c).width = 11;

    let fila = 1;

    // ---- Encabezado empresa ----
    ws.mergeCells(fila, 2, fila + 3, TOTAL_COLS);
    const celdaTitulo = ws.getCell(fila, 2);
    celdaTitulo.value = `${this.construirTituloEmpresa()}\nPLAN ESTRATÉGICO DE SEGURIDAD VIAL\nPLAN DE TRABAJO ANUAL FASE 2 - PASO 9`;
    celdaTitulo.alignment = {
      horizontal: "center",
      vertical: "middle",
      wrapText: true,
    };
    celdaTitulo.font = { bold: true, size: 12 };
    this.aplicarBordeRango(ws, 1, 2, 4, TOTAL_COLS);
    fila += 5;

    // ---- Banda verde con el nombre de la Fase ----
    ws.mergeCells(fila, 2, fila, TOTAL_COLS);
    const celdaFase = ws.getCell(fila, 2);
    celdaFase.value = nombreFase;
    celdaFase.font = { bold: true, size: 14, color: { argb: "FFFFFFFF" } };
    celdaFase.alignment = { horizontal: "center", vertical: "middle" };
    celdaFase.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: this.COLOR_VERDE_BANDA },
    };
    fila += 1;

    // ---- Encabezado de columnas: PASO | ACTIVIDAD | RESPONSABLE | meses ----
    const filaEncabezadoMeses = fila;
    const filaSubEncabezado = fila + 1;

    ws.mergeCells(filaEncabezadoMeses, COL_PASO, filaSubEncabezado, COL_PASO);
    ws.getCell(filaEncabezadoMeses, COL_PASO).value = "PASO";

    ws.mergeCells(
      filaEncabezadoMeses,
      COL_ACTIVIDAD,
      filaSubEncabezado,
      COL_ACTIVIDAD,
    );
    ws.getCell(filaEncabezadoMeses, COL_ACTIVIDAD).value = "ACTIVIDAD";

    ws.mergeCells(
      filaEncabezadoMeses,
      COL_RESPONSABLE,
      filaSubEncabezado,
      COL_RESPONSABLE,
    );
    ws.getCell(filaEncabezadoMeses, COL_RESPONSABLE).value = "RESPONSABLE";

    [COL_PASO, COL_ACTIVIDAD, COL_RESPONSABLE].forEach((c) => {
      const celda = ws.getCell(filaEncabezadoMeses, c);
      celda.font = { bold: true };
      celda.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
      celda.border = this.bordeDelgado();
    });

    this.MESES.forEach((mes, idx) => {
      const colP = COL_MESES_INICIO + idx * 2;
      const colE = colP + 1;

      ws.mergeCells(filaEncabezadoMeses, colP, filaEncabezadoMeses, colE);
      const celdaMes = ws.getCell(filaEncabezadoMeses, colP);
      celdaMes.value = mes;
      celdaMes.font = { bold: true };
      celdaMes.alignment = { horizontal: "center" };
      celdaMes.border = this.bordeDelgado();

      const celdaP = ws.getCell(filaSubEncabezado, colP);
      celdaP.value = "P";
      celdaP.font = { bold: true };
      celdaP.alignment = { horizontal: "center" };
      celdaP.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: this.COLOR_AMARILLO_P },
      };
      celdaP.border = this.bordeDelgado();

      const celdaE = ws.getCell(filaSubEncabezado, colE);
      celdaE.value = "E";
      celdaE.font = { bold: true };
      celdaE.alignment = { horizontal: "center" };
      celdaE.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: this.COLOR_VERDE_E },
      };
      celdaE.border = this.bordeDelgado();
    });

    fila = filaSubEncabezado + 1;

    // ---- Filas de datos: cada Paso fusionado verticalmente sobre sus actividades ----
    pasosFase.forEach((p: any) => {
      const nombrePaso = p?.PasoActividadPlanData?.paso ?? "";
      const actividades = Array.isArray(p?.ActividadPasosPlanreData)
        ? p.ActividadPasosPlanreData
        : [];

      const filaInicioPaso = fila;

      if (!actividades.length) {
        this.escribirFilaVacia(ws, fila, COL_RESPONSABLE, TOTAL_COLS);
        fila += 1;
      } else {
        actividades.forEach((a: any) => {
          ws.getCell(fila, COL_ACTIVIDAD).value = a?.actividad ?? "";
          ws.getCell(fila, COL_ACTIVIDAD).alignment = {
            wrapText: true,
            vertical: "top",
          };
          ws.getCell(fila, COL_RESPONSABLE).value =
            a?.ResponsablePlanData?.nombreCompleto ?? "";
          ws.getCell(fila, COL_RESPONSABLE).alignment = {
            vertical: "top",
            horizontal: "center",
          };

          // Muestra la FECHA PROGRAMADA en la columna P (amarilla)
          const mesProgramado = this.obtenerMesIndex(a?.fecha_programada);
          if (mesProgramado !== null) {
            const colP = COL_MESES_INICIO + mesProgramado * 2;
            const celda = ws.getCell(fila, colP);
            celda.value = this.formatearFechaCorta(a?.fecha_programada);
            celda.font = { bold: true, size: 8 };
            celda.alignment = { horizontal: "center", vertical: "middle" };
            celda.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: this.COLOR_AMARILLO_P },
            };
          }

          // Muestra la FECHA EJECUTADA más reciente (último seguimiento) en la columna E (verde)
          const fechaEjecucion = this.obtenerFechaEjecucionActividad(a);
          const mesEjecutado = this.obtenerMesIndex(fechaEjecucion);
          if (mesEjecutado !== null) {
            const colE = COL_MESES_INICIO + mesEjecutado * 2 + 1;
            const celda = ws.getCell(fila, colE);
            celda.value = this.formatearFechaCorta(fechaEjecucion);
            celda.font = { bold: true, size: 8 };
            celda.alignment = { horizontal: "center", vertical: "middle" };
            celda.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: this.COLOR_VERDE_E },
            };
          }

          // Bordes delgados en toda la fila
          for (let c = COL_PASO; c <= TOTAL_COLS; c++) {
            ws.getCell(fila, c).border = this.bordeDelgado();
          }

          fila += 1;
        });
      }

      const filaFinPaso = fila - 1;

      this.fusionarSeguro(ws, filaInicioPaso, COL_PASO, filaFinPaso, COL_PASO);
      const celdaPaso = ws.getCell(filaInicioPaso, COL_PASO);
      celdaPaso.value = nombrePaso;
      celdaPaso.font = { bold: true };
      celdaPaso.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
    });
  }

  private escribirFilaVacia(
    ws: ExcelJS.Worksheet,
    fila: number,
    colResponsable: number,
    totalCols: number,
  ): void {
    ws.getCell(fila, colResponsable - 1).value = "";
    for (let c = 2; c <= totalCols; c++) {
      ws.getCell(fila, c).border = this.bordeDelgado();
    }
  }

  private bordeDelgado(): Partial<ExcelJS.Borders> {
    const estilo: ExcelJS.BorderStyle = "thin";
    return {
      top: { style: estilo, color: { argb: "FFB0B0B0" } },
      left: { style: estilo, color: { argb: "FFB0B0B0" } },
      bottom: { style: estilo, color: { argb: "FFB0B0B0" } },
      right: { style: estilo, color: { argb: "FFB0B0B0" } },
    };
  }

  private formatearFechaCorta(fecha: any): string {
    if (!fecha) return "";
    const d = new Date(fecha);
    if (isNaN(d.getTime())) return "";
    const dia = String(d.getUTCDate()).padStart(2, "0");
    const mes = String(d.getUTCMonth() + 1).padStart(2, "0");
    const anio = d.getUTCFullYear();
    return `${dia}/${mes}/${anio}`;
  }

  // ===========================================================
  // INDICADORES: cálculo + fechas de ejecución
  // ===========================================================

  /** Fecha (fechaCreacion) del seguimiento más reciente de un arreglo, o null si está vacío. */
  private obtenerFechaMasRecienteSeguimiento(
    seguimientos: any[] | undefined,
  ): string | null {
    if (!Array.isArray(seguimientos) || !seguimientos.length) return null;

    let fechaMasReciente: string | null = null;
    let tiempoMasReciente = -Infinity;

    seguimientos.forEach((s: any) => {
      if (!s?.fechaCreacion) return;
      const t = new Date(s.fechaCreacion).getTime();
      if (!isNaN(t) && t > tiempoMasReciente) {
        tiempoMasReciente = t;
        fechaMasReciente = s.fechaCreacion;
      }
    });

    return fechaMasReciente;
  }

  private obtenerFechaEjecucionActividad(actividad: any): string | null {
    return this.obtenerFechaMasRecienteSeguimiento(
      actividad?.SeguimientoActividadData,
    );
  }

  private obtenerFechaEjecucionMeta(meta: any): string | null {
    return this.obtenerFechaMasRecienteSeguimiento(meta?.SeguimientosMetasData);
  }

  /** Indicador Paso 9 / Indicador 5 (Res. 40595 de 2022): % Cumplimiento de actividades del Plan Anual. */
  private calcularIndicadorCumplimientoActividades(data: any): {
    AEPlan: number;
    APPlan: number;
    porcentaje: number;
  } {
    const actividades = (
      Array.isArray(data?.ActividadPasosPlanData) ? data.ActividadPasosPlanData : []
    )
      .flatMap((p: any) =>
        Array.isArray(p?.ActividadPasosPlanreData) ? p.ActividadPasosPlanreData : [],
      )
      .filter((act: any) => act.estado === 1); // solo activas cuentan como "programadas"

    const APPlan = actividades.length;
    const AEPlan = actividades.filter(
      (act: any) => this.obtenerFechaEjecucionActividad(act) !== null,
    ).length;
    const porcentaje = APPlan > 0 ? (AEPlan / APPlan) * 100 : 0;

    return { AEPlan, APPlan, porcentaje: Math.round(porcentaje * 100) / 100 };
  }

  /** Indicador 4 (Res. 40595 de 2022): % Cumplimiento de metas del PESV. */
  private calcularIndicadorCumplimientoMetas(metas: MetasPlan[]): {
    MA: number;
    TM: number;
    porcentaje: number;
  } {
    const metasActivas = (metas || []).filter((m: any) => Number(m.estado) === 1);
    const TM = metasActivas.length;
    const MA = metasActivas.filter(
      (m: any) => this.obtenerFechaEjecucionMeta(m) !== null,
    ).length;
    const porcentaje = TM > 0 ? (MA / TM) * 100 : 0;

    return { MA, TM, porcentaje: Math.round(porcentaje * 100) / 100 };
  }
}